import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '../../api/api';
import {
  FiClock, FiUser, FiGlobe, FiMonitor, FiSmartphone,
  FiTablet, FiMapPin, FiCalendar, FiFilter, FiDownload,
  FiRefreshCw, FiEye, FiTrash2, FiAlertCircle, FiCheckCircle,
  FiXCircle, FiBarChart2, FiPieChart, FiActivity, FiShield
} from 'react-icons/fi';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const token = localStorage.getItem('token');

const LoginHistoryLayer = () => {
  // State management
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    user_type: '',
    status: '',
    device_type: '',
    browser: '',
    country: '',
    start_date: null,
    end_date: null,
    search: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 20
  });

  // Date range
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date()
  });

  // Stats date range
  const [statsDateRange, setStatsDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date()
  });

  // Initial fetch
  useEffect(() => {
    fetchLoginHistory();
    fetchDashboardData();
    fetchActiveSessions();
  }, []);

  // Fetch login history with filters
  // Fetch login history with filters
  const fetchLoginHistory = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };

      // Add filters only if they have values
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.status) params.status = filters.status;
      if (filters.device_type) params.device_type = filters.device_type;
      if (filters.browser) params.browser = filters.browser;
      if (filters.country) params.country = filters.country;
      if (filters.search) params.email = filters.search;

      // Add date filters if set
      if (filters.start_date) {
        params.start_date = filters.start_date.toISOString().split('T')[0];
      }
      if (filters.end_date) {
        params.end_date = filters.end_date.toISOString().split('T')[0];
      }

      console.log('Fetching login history with params:', params);

      const response = await axios.get(`${API_URL}/login-history`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Login history response:', response.data);

      // Handle both paginated and non-paginated responses
      if (response.data.data) {
        // Laravel paginated response
        setLoginHistory(response.data.data);
        setPagination({
          current_page: response.data.current_page || 1,
          total_pages: response.data.last_page || 1,
          total_items: response.data.total || 0,
          per_page: response.data.per_page || 20
        });
      } else if (Array.isArray(response.data)) {
        // Direct array response
        setLoginHistory(response.data);
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: response.data.length,
          per_page: response.data.length
        });
      } else {
        // Fallback
        setLoginHistory([]);
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: 0,
          per_page: 20
        });
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      console.error('Error response:', error.response);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load login history.'
      });
      setLoginHistory([]);
    } finally {
      setLoading(false);
    }
  };
  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/login-history/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const params = {
        start_date: statsDateRange.startDate.toISOString().split('T')[0],
        end_date: statsDateRange.endDate.toISOString().split('T')[0]
      };

      const response = await axios.get(`${API_URL}/login-stats`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load statistics.'
      });
    }
  };

  // Fetch active sessions
  const fetchActiveSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/active-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSessions(response.data.active_sessions || []);
    } catch (error) {
      console.error('Error fetching active sessions:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchLoginHistory(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      user_type: '',
      status: '',
      device_type: '',
      browser: '',
      country: '',
      start_date: null,
      end_date: null,
      search: ''
    });
    fetchLoginHistory(1);
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = {};
      if (filters.start_date) {
        params.start_date = filters.start_date.toISOString().split('T')[0];
      }
      if (filters.end_date) {
        params.end_date = filters.end_date.toISOString().split('T')[0];
      }

      const response = await axios.get(`${API_URL}/login-history/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `login_history_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Login history exported to CSV successfully.',
        timer: 2000
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to export login history.'
      });
    }
  };

  // Revoke session
  const revokeSession = async (tokenId) => {
    const result = await Swal.fire({
      title: 'Revoke Session?',
      text: 'This will log out the user from this device.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, revoke it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/revoke-session/${tokenId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: 'success',
          title: 'Revoked!',
          text: 'Session revoked successfully.',
          timer: 2000
        });

        fetchActiveSessions();
      } catch (error) {
        console.error('Error revoking session:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to revoke session.'
        });
      }
    }
  };

  // Logout all devices
  const logoutAllDevices = async () => {
    const result = await Swal.fire({
      title: 'Logout All Devices?',
      text: 'This will log out the user from all devices.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, logout all!'
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`${API_URL}/logout-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Logged out from all devices successfully.',
          timer: 2000
        });

        fetchActiveSessions();
      } catch (error) {
        console.error('Error logging out all devices:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to logout from all devices.'
        });
      }
    }
  };

  // Clear old history
  const clearOldHistory = async () => {
    const result = await Swal.fire({
      title: 'Clear Old History?',
      text: 'This will delete login history older than 90 days.',
      icon: 'warning',
      input: 'number',
      inputLabel: 'Days to keep (default: 90)',
      inputValue: 90,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear it!',
      inputValidator: (value) => {
        if (!value || value < 1) {
          return 'Please enter a valid number of days';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        const days = result.value || 90;
        await axios.delete(`${API_URL}/clear-old-history?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: 'success',
          title: 'Cleared!',
          text: `Old login history cleared successfully.`,
          timer: 2000
        });

        fetchLoginHistory();
      } catch (error) {
        console.error('Error clearing old history:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to clear old history.'
        });
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      success: { color: 'success', icon: <FiCheckCircle />, text: 'Success' },
      failed: { color: 'danger', icon: <FiXCircle />, text: 'Failed' },
      blocked: { color: 'warning', icon: <FiAlertCircle />, text: 'Blocked' }
    };

    const badge = badges[status] || badges.failed;

    return (
      <span className={`badge bg-${badge.color} d-flex align-items-center gap-1`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  // Get device icon
  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'desktop':
      case 'pc':
        return <FiMonitor />;
      case 'mobile':
        return <FiSmartphone />;
      case 'tablet':
        return <FiTablet />;
      default:
        return <FiMonitor />;
    }
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  const STATUS_COLORS = {
    success: '#10b981',
    failed: '#ef4444',
    blocked: '#f59e0b'
  };

  // Pie chart data for devices
  const deviceChartData = dashboardData?.top_browsers?.map((browser, index) => ({
    name: browser.browser || 'Unknown',
    value: browser.count,
    color: COLORS[index % COLORS.length]
  })) || [];

  // Line chart data for weekly trend
  const weeklyChartData = dashboardData?.weekly_trend?.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
    logins: item.count
  })) || [];

  // Render loading
  if (loading && !loginHistory.length) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading login history...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-gradient-primary text-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <FiClock className="me-2" />
                    Login History & Security Dashboard
                  </h5>
                  <p className="mb-0 opacity-75 small">
                    Monitor and manage user login activities
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    onClick={exportToCSV}
                    className="btn btn-light btn-sm"
                    title="Export to CSV"
                  >
                    <FiDownload className="me-1" /> Export
                  </button>
                  <button
                    onClick={() => fetchLoginHistory()}
                    className="btn btn-light btn-sm"
                    title="Refresh"
                  >
                    <FiRefreshCw />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboardData && (
        <div className="row mb-4">
          {/* Today's Overview */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-primary text-white h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 opacity-75">
                      <FiCalendar className="me-1" />
                      Today's Logins
                    </h6>
                    <h2 className="mb-0">{dashboardData.today?.total || 0}</h2>
                    <small className="opacity-75">
                      {dashboardData.today?.successful || 0} successful • {dashboardData.today?.failed || 0} failed
                    </small>
                  </div>
                  <FiActivity size={32} />
                </div>
                <div className="mt-3">
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${dashboardData.today?.success_rate || 0}%` }}
                    />
                  </div>
                  <small className="opacity-75">
                    {dashboardData.today?.success_rate?.toFixed(1) || 0}% success rate
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Yesterday Comparison */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-info text-white h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 opacity-75">
                      <FiBarChart2 className="me-1" />
                      Daily Change
                    </h6>
                    <h6 className="mb-0">
                      {dashboardData.yesterday_comparison?.change_percentage > 0 ? '+' : ''}
                      {dashboardData.yesterday_comparison?.change_percentage?.toFixed(1) || 0}%
                    </h6>
                    <small className="opacity-75">
                      vs yesterday
                    </small>
                  </div>
                  <FiBarChart2 size={32} />
                </div>
                <div className="mt-3">
                  <div className="d-flex justify-content-between">
                    <small>Yesterday: {dashboardData.yesterday_comparison?.yesterday_total || 0}</small>
                    <small>Today: {dashboardData.yesterday_comparison?.today_total || 0}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-success text-white h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 opacity-75">
                      <FiUser className="me-1" />
                      Active Sessions
                    </h6>
                    <h2 className="mb-0">{activeSessions.length}</h2>
                    <small className="opacity-75">
                      Currently logged in
                    </small>
                  </div>
                  <FiUser size={32} />
                </div>
                <div className="mt-3">
                  <button
                    onClick={logoutAllDevices}
                    className="btn btn-sm btn-light w-100"
                  >
                    <FiShield className="me-1" />
                    Logout All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Security Alerts */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-warning text-white h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 opacity-75">
                      <FiAlertCircle className="me-1" />
                      Suspicious Activities
                    </h6>
                    <h2 className="mb-0">
                      {dashboardData.recent_suspicious?.length || 0}
                    </h2>
                    <small className="opacity-75">
                      Last 6 hours
                    </small>
                  </div>
                  <FiAlertCircle size={32} />
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => fetchLoginHistory(1)}
                    className="btn btn-sm btn-light w-100"
                  >
                    <FiEye className="me-1" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="col-lg-8 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <FiActivity className="me-2" />
                  Weekly Login Trend
                </h6>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weeklyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="logins"
                      stroke="#0088FE"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <FiPieChart className="me-2" />
                  Top Browsers
                </h6>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={deviceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {deviceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light">
              <h6 className="mb-0">
                <FiFilter className="me-2" />
                Filters
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-2">
                  <label className="form-label">User Type</label>
                  <select
                    className="form-select"
                    value={filters.user_type}
                    onChange={(e) => handleFilterChange('user_type', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="user">User</option>
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Device</label>
                  <select
                    className="form-select"
                    value={filters.device_type}
                    onChange={(e) => handleFilterChange('device_type', e.target.value)}
                  >
                    <option value="">All Devices</option>
                    <option value="desktop">Desktop</option>
                    <option value="mobile">Mobile</option>
                    <option value="tablet">Tablet</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Browser</label>
                  <select
                    className="form-select"
                    value={filters.browser}
                    onChange={(e) => handleFilterChange('browser', e.target.value)}
                  >
                    <option value="">All Browsers</option>
                    <option value="Chrome">Chrome</option>
                    <option value="Firefox">Firefox</option>
                    <option value="Safari">Safari</option>
                    <option value="Edge">Edge</option>
                    <option value="Opera">Opera</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Start Date</label>
                  <DatePicker
                    selected={filters.start_date}
                    onChange={(date) => handleFilterChange('start_date', date)}
                    className="form-control"
                    placeholderText="Select start date"
                    maxDate={new Date()}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">End Date</label>
                  <DatePicker
                    selected={filters.end_date}
                    onChange={(date) => handleFilterChange('end_date', date)}
                    className="form-control"
                    placeholderText="Select end date"
                    maxDate={new Date()}
                    minDate={filters.start_date}
                  />
                </div>

                <div className="col-md-8">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by email, IP, or location..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>

                <div className="col-md-4 d-flex align-items-end gap-2">
                  <button
                    onClick={applyFilters}
                    className="btn btn-primary w-50"
                  >
                    <FiFilter className="me-1" />
                    Apply Filters
                  </button>
                  <button
                    onClick={resetFilters}
                    className="btn btn-outline-secondary w-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <FiUser className="me-2" />
                  Active Sessions ({activeSessions.length})
                </h6>
                <button
                  onClick={logoutAllDevices}
                  className="btn btn-sm btn-outline-danger"
                >
                  <FiShield className="me-1" />
                  Logout All Devices
                </button>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>IP Address</th>
                        <th>Location</th>
                        <th>Login Time</th>
                        <th>Last Activity</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.token_id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {getDeviceIcon(session.device_type)}
                              <div className="ms-2">
                                <div className="fw-semibold">{session.device_name}</div>
                                <small className="text-muted">
                                  {session.browser} on {session.platform}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <code>{session.ip_address}</code>
                          </td>
                          <td>
                            {session.city && session.country ? (
                              <div>
                                <FiMapPin className="me-1" />
                                {session.city}, {session.country}
                              </div>
                            ) : (
                              <span className="text-muted">Unknown</span>
                            )}
                          </td>
                          <td>
                            <div className="text-nowrap">
                              {session.login_at ? formatDate(session.login_at) : 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-nowrap">
                              {session.last_used_human}
                            </div>
                          </td>
                          <td>
                            {session.is_current ? (
                              <span className="badge bg-success">Current</span>
                            ) : (
                              <span className="badge bg-info">Active</span>
                            )}
                          </td>
                          <td>
                            {!session.is_current && (
                              <button
                                onClick={() => revokeSession(session.token_id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Revoke Session"
                              >
                                <FiTrash2 />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login History Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FiClock className="me-2" />
                Login History ({pagination.total_items} records)
              </h6>
              <div className="d-flex gap-2">
                <button
                  onClick={clearOldHistory}
                  className="btn btn-sm btn-outline-warning"
                  title="Clear old history"
                >
                  <FiTrash2 className="me-1" />
                  Clear Old
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Type</th>
                      <th>Device & Browser</th>
                      <th>IP & Location</th>
                      <th>Login Time</th>
                      <th>Session Duration</th>
                      <th>Status</th>
                      <th>Failure Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <FiClock size={48} className="text-muted mb-3" />
                          <h5 className="text-muted">No login history found</h5>
                          <p className="text-muted">Try adjusting your filters</p>
                        </td>
                      </tr>
                    ) : (
                      loginHistory.map((history) => (
                        <tr key={history.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center"
                                  style={{ width: '40px', height: '40px' }}>
                                  <FiUser />
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <div className="fw-semibold">
                                  {history.user?.name || history.employee?.name || 'Unknown User'}
                                </div>
                                <small className="text-muted">
                                  {history.email || history.mobile || 'N/A'}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge bg-${history.user_type === 'employee' ? 'info' : 'primary'}`}>
                              {history.user_type}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="me-2">
                                {getDeviceIcon(history.device_type)}
                              </span>
                              <div>
                                <div className="fw-semibold">{history.device_type || 'Unknown'}</div>
                                <small className="text-muted">
                                  {history.browser} • {history.platform}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="fw-semibold">
                                <code>{history.ip_address}</code>
                              </div>
                              <small className="text-muted">
                                {history.city && history.country ? (
                                  <>
                                    <FiMapPin className="me-1" />
                                    {history.city}, {history.country}
                                  </>
                                ) : (
                                  'Location unknown'
                                )}
                              </small>
                            </div>
                          </td>
                          <td>
                            <div className="text-nowrap">
                              {formatDate(history.login_at)}
                            </div>
                            {history.logout_at && (
                              <small className="text-muted d-block">
                                Logout: {formatDate(history.logout_at)}
                              </small>
                            )}
                          </td>
                          <td>
                            {formatDuration(history.session_duration)}
                          </td>
                          <td>
                            {getStatusBadge(history.status)}
                          </td>
                          <td>
                            {history.failure_reason ? (
                              <span className="badge bg-danger">
                                <FiAlertCircle className="me-1" />
                                {history.failure_reason}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <nav className="d-flex justify-content-center mt-4">
                  <ul className="pagination">
                    <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => fetchLoginHistory(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {[...Array(Math.min(5, pagination.total_pages)).keys()].map((num) => {
                      const pageNum = Math.max(1, Math.min(
                        pagination.current_page - 2,
                        pagination.total_pages - 4
                      )) + num;

                      return (
                        <li key={pageNum} className={`page-item ${pagination.current_page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => fetchLoginHistory(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${pagination.current_page === pagination.total_pages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => fetchLoginHistory(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.total_pages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
            <div className="card-footer bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Page {pagination.current_page} of {pagination.total_pages} •
                  Showing {loginHistory.length} of {pagination.total_items} records
                </small>
                <small className="text-muted">
                  {pagination.per_page} per page
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light">
              <h6 className="mb-0">
                <FiBarChart2 className="me-2" />
                Advanced Statistics
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label">Start Date</label>
                  <DatePicker
                    selected={statsDateRange.startDate}
                    onChange={(date) => setStatsDateRange(prev => ({ ...prev, startDate: date }))}
                    className="form-control"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">End Date</label>
                  <DatePicker
                    selected={statsDateRange.endDate}
                    onChange={(date) => setStatsDateRange(prev => ({ ...prev, endDate: date }))}
                    className="form-control"
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    onClick={fetchStats}
                    className="btn btn-primary w-100"
                  >
                    <FiBarChart2 className="me-1" />
                    Generate Stats
                  </button>
                </div>
              </div>

              {stats && (
                <div className="row">
                  <div className="col-md-6">
                    <h6>Overview</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <tbody>
                          <tr>
                            <td>Total Logins</td>
                            <td className="fw-bold">{stats.overview?.total_logins}</td>
                          </tr>
                          <tr>
                            <td>Successful Logins</td>
                            <td className="fw-bold text-success">{stats.overview?.successful_logins}</td>
                          </tr>
                          <tr>
                            <td>Failed Logins</td>
                            <td className="fw-bold text-danger">{stats.overview?.failed_logins}</td>
                          </tr>
                          <tr>
                            <td>Success Rate</td>
                            <td className="fw-bold">{stats.overview?.success_rate}%</td>
                          </tr>
                          <tr>
                            <td>Unique Users</td>
                            <td className="fw-bold">{stats.overview?.unique_users}</td>
                          </tr>
                          <tr>
                            <td>Unique IPs</td>
                            <td className="fw-bold">{stats.overview?.unique_ips}</td>
                          </tr>
                          <tr>
                            <td>Avg Session Duration</td>
                            <td className="fw-bold">
                              {stats.overview?.avg_session_duration_minutes?.toFixed(1)} minutes
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h6>Device Distribution</h6>
                    {stats.device_stats && Object.keys(stats.device_stats).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={Object.entries(stats.device_stats).map(([device, count]) => ({ device, count }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="device" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#0088FE" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted">No device data available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginHistoryLayer;